import { AlertCircle, Building2, Mail, Ticket as TicketIcon } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { TimeEntryForm } from '@/components/TimeEntryForm';
import { useContactLookup, useTicketAdditionalContacts } from '@/api/hooks';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ticketId: number;
  ticketNumber?: string | null;
  ticketTitle?: string | null;
  companyName?: string | null;
  /** The ticket's primary contact — first in line for the customer-visible note. */
  contactId?: number | null;
  /** 0 = Autotask's internal company; time logged there can't be read back via the API. */
  companyId?: number | null;
  /** Session timer start, so the modal logs the time already spent on this ticket. */
  startTime: Date;
}

function ContextRow({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2">
      <span className="mt-[3px] shrink-0 text-muted-foreground">{icon}</span>
      <div className="min-w-0">
        <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="text-[13px] leading-snug text-foreground">{children}</div>
      </div>
    </div>
  );
}

export function LogTimeDialog({
  open,
  onOpenChange,
  ticketId,
  ticketNumber,
  ticketTitle,
  companyName,
  contactId,
  companyId,
  startTime,
}: Props) {
  // Only queried while the modal is open — additional contacts aren't needed anywhere else.
  const additional = useTicketAdditionalContacts(ticketId, open);
  const additionalIds = (additional.data?.items ?? []).map((c) => c.contactID).filter((v) => v != null);
  const recipientIds = open ? [...(contactId != null ? [contactId] : []), ...additionalIds] : [];
  const contacts = useContactLookup(recipientIds);

  const recipients = recipientIds
    .map((cid) => {
      const c = contacts.data?.items.find((x) => x.id === cid);
      if (!c) return null;
      const name = `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim() || `#${cid}`;
      return { id: cid, name, email: c.emailAddress ?? null, primary: cid === contactId };
    })
    .filter((r): r is { id: number; name: string; email: string | null; primary: boolean } => r != null);

  // Named in the form's confirmation step. Falls back to the client, then a generic phrase —
  // the summary is customer-visible regardless of whether a contact resolved.
  const audience =
    recipients.length === 0
      ? companyName ?? null
      : recipients.length === 1
      ? recipients[0].name
      : `${recipients[0].name} and ${recipients.length - 1} other${recipients.length > 2 ? 's' : ''}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Log time</DialogTitle>
        </DialogHeader>

        <div className="max-h-[80vh] overflow-y-auto px-6 pb-6">
          {/* Who/what this entry lands against — the summary goes to a real customer, so
              show the ticket, the client, and everyone Autotask will notify. */}
          <div className="space-y-2.5 rounded-md border border-border bg-muted/30 p-3">
            <ContextRow icon={<TicketIcon className="h-3.5 w-3.5"/>} label="Ticket">
              <span className="font-mono text-xs">{ticketNumber ?? `#${ticketId}`}</span>
              {ticketTitle ? <span className="ml-2 text-muted-foreground">{ticketTitle}</span> : null}
            </ContextRow>

            <ContextRow icon={<Building2 className="h-3.5 w-3.5"/>} label="Client">
              {companyName ?? '—'}
            </ContextRow>

            <ContextRow icon={<Mail className="h-3.5 w-3.5"/>} label="Summary is emailed to">
              {recipients.length === 0 ? (
                <span className="inline-flex items-center gap-1.5 text-orange-600 dark:text-orange-400">
                  <AlertCircle className="h-3.5 w-3.5"/>
                  {contactId == null
                    ? 'No contact on this ticket — nothing will be emailed'
                    : (contacts.isLoading || additional.isLoading ? 'Loading contacts…' : 'Contact could not be resolved')}
                </span>
              ) : (
                <ul className="space-y-0.5">
                  {recipients.map((r) => (
                    <li key={r.id} className="flex flex-wrap items-baseline gap-x-1.5">
                      <span>{r.name}</span>
                      {r.email ? <span className="text-[11px] text-muted-foreground">{r.email}</span> : null}
                      {!r.primary ? <span className="text-[10px] uppercase tracking-wider text-muted-foreground">cc</span> : null}
                    </li>
                  ))}
                </ul>
              )}
            </ContextRow>
          </div>

          <p className="mt-2 text-[11px] leading-snug text-muted-foreground">
            Summary is posted as a customer-visible ticket note. Internal notes stay on the timesheet.
          </p>

          {companyId === 0 && (
            <p className="mt-2 text-[11px] leading-snug text-orange-600 dark:text-orange-400">
              Internal ticket: the entry will save, but Autotask's API won't return it, so it
              won't appear in the Time tab - check it in Autotask.
            </p>
          )}

          <div className="mt-3">
            <TimeEntryForm
              ticketId={ticketId}
              startTime={startTime}
              variant="plain"
              onLogged={() => onOpenChange(false)}
              customerAudience={audience}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
